import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Priority, Profile, Status } from '../types';
import ProgressTracker from '../components/ProgressTracker';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';

export default function CreateIssue() {
  const { user, profile, role } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('Medium');
  const [status, setStatus] = useState<Status>('Open');
  const [images, setImages] = useState<File[]>([]);
  const [developers, setDevelopers] = useState<Profile[]>([]);
  const [testers, setTesters] = useState<Profile[]>([]);
  const [assignedDeveloperId, setAssignedDeveloperId] = useState<string>('');
  const [assignedTesterId, setAssignedTesterId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    const loadMembers = async () => {
      try {
        const allProfiles = await api.get<Profile[]>('/api/users');
        const devs = allProfiles.filter(p => p.role === 'developer' || p.role === 'admin');
        const qas  = allProfiles.filter(p => p.role === 'tester');
        setDevelopers(devs);
        setTesters(qas);

        if (role === 'admin') {
          setAssignedDeveloperId(devs[0]?.user_id ?? user?.id ?? '');
        } else {
          setAssignedDeveloperId(user?.id ?? '');
        }
      } catch (err: any) {
        showToast(err?.message || 'Failed to load team members.', 'error');
      }
    };
    loadMembers();
  }, [profile?.company_id, role, user?.id]);

  const uploadImages = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { url } = await api.upload<{ url: string }>('/api/upload', formData);
        urls.push(url);
      } catch (err: any) {
        showToast(`Failed to upload ${file.name}: ${err?.message ?? 'Unknown error'}`, 'error');
      }
    }
    return urls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      showToast('Please provide a title for the issue.', 'error');
      return;
    }
    setSubmitting(true);

    const imageUrls = images.length > 0 ? await uploadImages(images) : [];

    try {
      await api.post('/api/issues', {
        title:                 title.trim(),
        description,
        priority,
        status,
        assigned_developer_id: assignedDeveloperId || user!.id,
        assigned_tester_id:    assignedTesterId || null,
        review_status:         assignedTesterId ? 'ready_for_review' : 'not_required',
        images:                imageUrls,
        project_id:            projectId ?? null,
      });

      showToast('Issue created successfully!', 'success');
      navigate(projectId ? `/projects/${projectId}` : '/');
    } catch (err: any) {
      showToast(err?.message || 'Failed to create issue.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(projectId ? `/projects/${projectId}` : '/')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          {projectId ? 'Back to Project' : 'Projects'}
        </button>
        <h1 className="page-title">Create Issue</h1>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="title">
              Title <span className="required">*</span>
            </label>
            <input
              id="title"
              type="text"
              className="form-input"
              placeholder="Short, descriptive title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              required
            />
            <span className="char-count">{title.length}/200</span>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe the issue in detail..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="priority">
                Priority
                <span className="tooltip" data-tip="How urgent is this issue?">?</span>
              </label>
              <select
                id="priority"
                className="form-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
              >
                <option value="High">🔴 High</option>
                <option value="Medium">🟡 Medium</option>
                <option value="Low">🟢 Low</option>
              </select>
            </div>
          </div>
          {role === 'admin' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="assignedDeveloper">Assign Developer</label>
                <select
                  id="assignedDeveloper"
                  className="form-select"
                  value={assignedDeveloperId}
                  onChange={(e) => setAssignedDeveloperId(e.target.value)}
                >
                  {developers.map((dev) => (
                    <option key={dev.user_id} value={dev.user_id}>
                      {dev.full_name || dev.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="assignedTester">Assign Tester (optional)</label>
                <select
                  id="assignedTester"
                  className="form-select"
                  value={assignedTesterId}
                  onChange={(e) => setAssignedTesterId(e.target.value)}
                >
                  <option value="">None</option>
                  {testers.map((tester) => (
                    <option key={tester.user_id} value={tester.user_id}>
                      {tester.full_name || tester.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Progress
              <span className="tooltip" data-tip="Track the current status of the issue">?</span>
            </label>
            <ProgressTracker status={status} onChange={setStatus} />
          </div>

          <div className="form-group">
            <label className="form-label">
              Attachments
              <span className="tooltip" data-tip="Add screenshots or images to clarify the issue">?</span>
            </label>
            <ImageUploader images={images} onChange={setImages} />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="btn-spinner" />
                  Creating…
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create Issue
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
